import React, { FunctionComponent, useState, useEffect } from 'react'
import { Button, Input, notification, Rate, Select, Space, Typography } from 'antd'

import LoginForm from '../../public/login/LoginForm/LoginForm'
import CustomModal from '../CustomModal'

import { useAuthContext } from '../../../context/auth'
import { fetchPastCalls, leaveReview } from '../../../context/review'

import { IUser, TCallList } from '../../../context/types'

import moment from 'moment'
import RegisterForm from '../../public/register/RegisterForm/RegisterForm'

const { TextArea } = Input
const { Title, Paragraph } = Typography
const { Option } = Select

interface ReviewModalProps {
	visible: boolean
	expertUser: IUser
	call_id?: string
	call_name?: string
	onAction: () => void
	onCancel: () => void
}

const ReviewModal: FunctionComponent<ReviewModalProps> = (props) => {
	const { visible, expertUser, call_id, call_name, onAction, onCancel } = props
	const { auth } = useAuthContext()

	const [registerForm, setRegisterForm] = useState<boolean>(false)
	const [callsList, setCallsList] = useState<TCallList[]>([])

	const [rating, setRating] = useState<number>(0)
	const [message, setMessage] = useState<string>()
	const [callId, setCallId] = useState<string>()
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		setRating(0)
		setMessage('')
		setCallId(undefined)

		if (visible && !call_id)
			fetchPastCalls(expertUser.id)
				.then((r) => setCallsList(r))
				.catch()

		if (call_id) setCallId(call_id)
	}, [visible, call_id, expertUser])

	const handleAction = () => {
		setLoading(true)
		leaveReview(expertUser.id, message, rating, callId)
			.then(() => {
				notification.success({ message: 'Success' })
				onAction()
				onCancel()
			})
			.catch((error) => {
				notification.error({ message: 'Error', description: error.message || 'Oops! Something went wrong!' })
			})
			.finally(() => setLoading(false))
	}

	const onGoToRegister = () => setRegisterForm(true)
	const onGoToLogin = () => setRegisterForm(false)

	return (
		<CustomModal destroyOnClose visible={visible} onCancel={onCancel} width={auth.token ? 950 : 600}>
			<div>
				<Title style={{ marginBottom: 25 }} level={3}>
					{auth.token ? 'Leave Review' : 'Please LoginForm or Register'}
				</Title>

				{!auth.token && !registerForm && (
					<div style={{ maxWidth: 480, margin: 'auto', width: '100%' }}>
						<LoginForm onGoToRegister={onGoToRegister} skipRedirect={true} />
					</div>
				)}

				{!auth.token && registerForm && (
					<div style={{ maxWidth: 480, margin: 'auto', width: '100%' }}>
						<RegisterForm onGoToLogin={onGoToLogin} skipRedirect={true} />
					</div>
				)}

				{!!auth.token && (
					<Space direction="vertical" style={{ width: '100%' }}>
						<div>
							<Title type="secondary" level={5} style={{ marginTop: 20 }}>
								Rating
							</Title>
							<Rate className="expert-rate" value={rating} onChange={setRating} />
						</div>
						<div>
							<p>&nbsp;</p>
							<TextArea
								autoSize={{ minRows: 4, maxRows: 8 }}
								placeholder={'Please explain your review in detail'}
								value={message}
								onChange={(e) => setMessage(e.target.value)}
							/>
						</div>
						<div>
							{(!!call_name || !!callsList.length) && (
								<Title type="secondary" level={5} style={{ marginTop: 20 }}>
									About a call
								</Title>
							)}
							{!!call_name && <Paragraph>{call_name}</Paragraph>}
							{!call_name && !!callsList.length && (
								<Select
									allowClear
									defaultValue={call_id}
									disabled={!callsList.length || !!call_id}
									style={{ width: 320 }}
									placeholder="Select a call"
									onSelect={(val) => setCallId(val as string)}
									onClear={() => setCallId(undefined)}
								>
									{callsList.map((call) => (
										<Option key={call.id} value={call.id}>
											{moment(call.start_time).format('ll') + ' - ' + call.reason}
										</Option>
									))}
								</Select>
							)}
						</div>
					</Space>
				)}

				<footer>
					{auth.token && (
						<Space direction="horizontal" style={{ marginTop: 25 }}>
							<Button type="dashed" shape="round" size="large" onClick={onCancel}>
								Cancel
							</Button>
							<Button
								type="primary"
								shape="round"
								size="large"
								onClick={handleAction}
								disabled={!message || !rating}
								loading={loading}
							>
								Submit
							</Button>
						</Space>
					)}
				</footer>
			</div>
		</CustomModal>
	)
}

export default ReviewModal
